<template>
  <div class="timeline-scroll">
    <div
      :style="{
        width: `${timelineSidebarWidth}px`,
      }"
      class="scroll-sidebar-placeholder"
    />
    <div
      ref="timelineScrollRef"
      class="timeline-scroll-wrapper"
      @click="handleScrollClick"
    >
      <div
        class="timeline-scroll-bar"
        :style="{
          width: `${scrollBarWidth}px`,
          left: `${scrollBarLeft}px`,
        }"
        @mousedown="handleScrollBarMouseDown"
        @touchstart="handleScrollBarTouchStart"
      />
    </div>
  </div>
</template>

<script>
import timelineDragMixin from '@/mixins/timelineDragMixin';

export default {
  name: 'TimelineHorizontalScroll',
  mixins: [timelineDragMixin],
  props: {
    scrollingRange: {
      type: Object,
      required: false,
      default: () => ({
        left: 0,
        right: 0,
      }),
    },
    position: {
      type: Number,
      required: true,
    },
  },
  emits: ['scrollBy', 'scrollTo'],
  data() {
    return {
      scrollContainerWidth: 0,
      eventType: '',
      isScrollBarFocused: false,
      requestAnimationFrameId: undefined,
      preventContainerScrollClick: false,
    };
  },
  computed: {
    timelineSidebarWidth() {
      return this.$store.getters.getTimelineSidebarWidth;
    },
    scrollingRangeWidth() {
      return this.scrollingRange.right - this.scrollingRange.left;
    },
    scrollBarWidth() {
      const { scrollContainerWidth, scrollingRangeWidth } = this;
      return (
        (scrollContainerWidth * scrollContainerWidth) / (scrollingRangeWidth + scrollContainerWidth)
      );
    },
    scrollBarWidthCenter() {
      return this.scrollBarWidth / 2;
    },
    scrollBarWidthCenterOffset() {
      return this.scrollContainerWidth - this.scrollBarWidthCenter;
    },
    scrollBarPosition() {
      if (this.scrolling === -1) {
        return 0;
      }
      return this.scrolling === 1
        ? this.containerWidthWithoutScrollBar
        : (this.containerWidthWithoutScrollBar * (this.position - this.scrollingRange.left)) /
            this.scrollingRangeWidth;
    },
    containerWidthWithoutScrollBar() {
      return this.scrollContainerWidth - this.scrollBarWidth;
    },
    scrollBarLeft() {
      return Math.max(0, Math.min(this.scrollBarPosition, this.containerWidthWithoutScrollBar));
    },
  },
  mounted() {
    this.scrollContainerObserver = new ResizeObserver(this.setScrollContainerWidth).observe(
      this.$refs.timelineScrollRef
    );
  },
  beforeUnmount() {
    this.scrollContainerObserver?.disconnect();
    this.removeScrollBarEvents();
  },
  methods: {
    handleScrollClick(event) {
      if (this.preventContainerScrollClick) {
        this.preventContainerScrollClick = false;
        return;
      }
      const { timelineScrollRef } = this.$refs;
      const { target, clientX } = event;
      if (!this.scrollContainerWidth || !timelineScrollRef || timelineScrollRef !== target) return;
      const { left } = timelineScrollRef.getBoundingClientRect();
      const offset = clientX - left;
      const position =
        this.scrollingRange.left + (this.scrollingRangeWidth * offset) / this.scrollContainerWidth;
      this.$emit('scrollTo', position);
    },
    setScrollContainerWidth() {
      const { timelineScrollRef } = this.$refs;
      if (!timelineScrollRef) return;
      this.scrollContainerWidth = timelineScrollRef.clientWidth;
    },
    handleScrollBarMouseDown(e) {
      this.isScrollBarFocused = true;
      this.eventType = 'mouse';
      this.start(this.eventType, e.clientX);
      document.addEventListener('mousemove', this.handleScrollBarMouseMove, false);
      document.addEventListener('mouseup', this.handleScrollBarMouseUp, false);
    },
    handleScrollBarTouchStart(e) {
      this.isScrollBarFocused = true;
      this.eventType = 'touch';
      this.start(this.eventType, e.touches[0].clientX);
      document.addEventListener('touchmove', this.handleScrollBarTouchMove, false);
      document.addEventListener('touchend', this.handleScrollBarTouchUp, false);
    },
    handleScrollBarMouseMove(event) {
      if (!event) return;
      this.onTimelineScroll(event.clientX);
    },
    handleScrollBarTouchMove(event) {
      if (!event) return;
      const touch = event.touches[0];
      this.onTimelineScroll(touch.clientX);
    },
    handleScrollBarMouseUp(event) {
      this.isScrollBarFocused = false;
      if (this.$refs.timelineScrollRef === event.target) {
        this.preventContainerScrollClick = true;
      }
      this.removeScrollBarEvents();
    },
    handleScrollBarTouchUp(event) {
      this.isScrollBarFocused = false;
      if (this.$refs.timelineScrollRef === event.target) {
        this.preventContainerScrollClick = true;
      }
      this.removeScrollBarEvents();
    },
    removeScrollBarEvents() {
      const { eventType } = this;
      if (!eventType) return;

      if (eventType === 'mouse') {
        document.removeEventListener('mousemove', this.handleScrollBarMouseMove, false);
        document.removeEventListener('mouseup', this.handleScrollBarMouseUp, false);
      } else if (eventType === 'touch') {
        document.removeEventListener('touchmove', this.handleScrollBarTouchMove, false);
        document.removeEventListener('touchend', this.handleScrollBarTouchUp, false);
      }
      this.eventType = '';
    },
    onTimelineScroll(clientX) {
      const draggingPosition = clientX - this.initialClientX;
      this.initialClientX = clientX;
      if (draggingPosition === 0) return;
      const direction = draggingPosition > 0 ? 1 : -1;
      const {
        requestAnimationFrameId,
        scrollBarWidthCenter,
        scrollBarWidthCenterOffset,
        containerWidthWithoutScrollBar,
        scrollingRangeWidth,
        scrollingRange,
        position,
        scrollContainerWidth,
      } = this;
      if (requestAnimationFrameId) {
        const { timelineScrollRef } = this.$refs;
        const { left, right } = timelineScrollRef.getBoundingClientRect();
        if (clientX < left || clientX > right || direction !== this.scrolling) {
          this.endAnimateScrolling();
        }
        return;
      }
      const currentScrollPosition =
        scrollBarWidthCenter +
        (containerWidthWithoutScrollBar * (position - scrollingRange.left)) / scrollingRangeWidth;
      if (direction === -1 && currentScrollPosition <= scrollBarWidthCenter) {
        this.startAnimateScrolling(-1);
        return;
      }
      if (direction === 1 && currentScrollPosition >= scrollBarWidthCenterOffset) {
        this.startAnimateScrolling(1);
        return;
      }
      this.$emit('scrollBy', (draggingPosition * scrollingRangeWidth) / scrollContainerWidth);
    },
    startAnimateScrolling(direction) {
      this.startScrolling(direction);
      let startTime = performance.now();
      const animate = () => {
        this.requestAnimationFrameId = requestAnimationFrame((currentTime) => {
          this.$emit(
            'scrollBy',
            direction * (currentTime - startTime) * this.$constants.timelineAnimatedScrollingSpeed
          );
          startTime = currentTime;
          animate();
        });
      };
      animate();
    },
    endAnimateScrolling() {
      this.endScrolling();
      if (this.requestAnimationFrameId) {
        cancelAnimationFrame(this.requestAnimationFrameId);
        this.requestAnimationFrameId = undefined;
      }
    },
  },
};
</script>

<style lang="scss">
.timeline-scroll {
  @apply flex;

  .scroll-sidebar-placeholder {
    border-right: 1px solid;
    @apply bg-background-strongest border-dividers-default;
    flex: 0 0 auto;
  }

  .timeline-scroll-wrapper {
    border-top: 1px solid;
    @apply bg-background-strongest relative overflow-hidden border-dividers-default;
    flex: 1 1 auto;
    height: 15px;
    z-index: 1;

    .timeline-scroll-bar {
      @apply absolute rounded-xl bg-scrollBar-default;
      top: 3px;
      bottom: 3px;

      &:hover {
        background-color: rgb(135, 135, 135);
      }
    }
  }
}
</style>
